import { useCallback, useEffect, useRef, useState } from 'react';
import { syncMemory, unsyncMemory } from '../services/memoryApi';
import { MEMORY_SYNC_MS, MEMORY_UNSYNC_MS, parseMemoryColdContext } from '../utils/memorySyncUtils';

/** @typedef {'off' | 'hot' | 'cold'} MemorySegment */

function defaultEntry() {
  return {
    /** Committed mode after WS or fallback; null means "only use seed" */
    mode: null,
    syncing: false,
    pendingTarget: null,
    fromMode: null,
    awaitingTaskId: null,
    expectedStatus: null
  };
}

function getFallbackMs(expectedStatus) {
  if (!expectedStatus) return MEMORY_SYNC_MS.hot;
  if (expectedStatus.includes('UNSYNC')) return MEMORY_UNSYNC_MS;
  if (expectedStatus === 'COLD_SYNC_COMPLETED') return MEMORY_SYNC_MS.cold;
  return MEMORY_SYNC_MS.hot;
}

/**
 * Backend may emit layer-specific COMPLETED strings or generic SYNC_COMPLETE / UNSYNC_COMPLETE.
 * taskId on the message must still match the single execution we track (see swap: POST taskId only).
 */
function wsStatusMatchesCompletion(status, expectedStatus) {
  if (!expectedStatus || !status) return false;
  if (status === expectedStatus) return true;
  if (
    status === 'SYNC_COMPLETE' &&
    (expectedStatus === 'HOT_SYNC_COMPLETED' || expectedStatus === 'COLD_SYNC_COMPLETED')
  ) {
    return true;
  }
  if (
    status === 'UNSYNC_COMPLETE' &&
    (expectedStatus === 'HOT_UNSYNC_COMPLETED' || expectedStatus === 'COLD_UNSYNC_COMPLETED')
  ) {
    return true;
  }
  return false;
}

/**
 * Mutually exclusive memory mode per file (OFF | Rules/hot | Reference/cold).
 * Unlocks on WebSocket status_update when taskId + status match, with timer fallback.
 */
export function useMemorySegmentController({ idToken, networkId, connectionId }) {
  const [byKey, setByKey] = useState({});
  const byKeyRef = useRef(byKey);
  byKeyRef.current = byKey;
  const timersRef = useRef(new Map());

  const clearFallback = useCallback((key) => {
    const tid = timersRef.current.get(key);
    if (tid) {
      clearTimeout(tid);
      timersRef.current.delete(key);
    }
  }, []);

  const scheduleFallback = useCallback(
    (key, expectedStatus, toMode) => {
      clearFallback(key);
      const ms = getFallbackMs(expectedStatus);
      const tid = setTimeout(() => {
        timersRef.current.delete(key);
        setByKey((prev) => {
          const cur = prev[key];
          if (!cur?.syncing) return prev;
          return {
            ...prev,
            [key]: {
              ...cur,
              mode: toMode,
              syncing: false,
              pendingTarget: null,
              fromMode: null,
              awaitingTaskId: null,
              expectedStatus: null
            }
          };
        });
      }, ms);
      timersRef.current.set(key, tid);
    },
    [clearFallback]
  );

  useEffect(() => {
    return () => {
      timersRef.current.forEach((tid) => clearTimeout(tid));
      timersRef.current.clear();
    };
  }, []);

  useEffect(() => {
    setByKey({});
  }, [networkId]);

  /**
   * WebSocket: only messages whose taskId === awaitingTaskId are applied (per row).
   * - HOT→COLD swap: DELETE returns taskId A, POST returns taskId B; we store B only — messages
   *   for A are ignored here (no matching awaitingTaskId), so DELETE "complete" never unlocks early.
   * - HOT_SYNC_STARTED / COLD_SYNC_STARTED: reset fallback timer when sync actually starts.
   * - Completion: HOT_SYNC_COMPLETED | COLD_SYNC_COMPLETED | HOT_UNSYNC_COMPLETED | COLD_UNSYNC_COMPLETED,
   *   or aliases SYNC_COMPLETE | UNSYNC_COMPLETE (see wsStatusMatchesCompletion).
   * - If the backend sends no COMPLETE, scheduleFallback still unlocks.
   */
  useEffect(() => {
    const onMsg = (e) => {
      const data = e.detail || {};
      if (data.type !== 'status_update' || !data.taskId) return;
      const { taskId, status } = data;

      for (const key of Object.keys(byKeyRef.current)) {
        const entry = byKeyRef.current[key];
        if (!entry?.syncing || entry.awaitingTaskId !== taskId) continue;

        if (wsStatusMatchesCompletion(status, entry.expectedStatus)) {
          clearFallback(key);
          setByKey((prev) => ({
            ...prev,
            [key]: {
              ...entry,
              mode: entry.pendingTarget,
              syncing: false,
              pendingTarget: null,
              fromMode: null,
              awaitingTaskId: null,
              expectedStatus: null
            }
          }));
          return;
        }

        if (status === 'HOT_SYNC_STARTED' && entry.expectedStatus === 'HOT_SYNC_COMPLETED') {
          clearFallback(key);
          scheduleFallback(key, 'HOT_SYNC_COMPLETED', entry.pendingTarget);
          return;
        }
        if (status === 'COLD_SYNC_STARTED' && entry.expectedStatus === 'COLD_SYNC_COMPLETED') {
          clearFallback(key);
          scheduleFallback(key, 'COLD_SYNC_COMPLETED', entry.pendingTarget);
          return;
        }
      }
    };
    window.addEventListener('websocket-message', onMsg);
    return () => window.removeEventListener('websocket-message', onMsg);
  }, [clearFallback, scheduleFallback]);

  const runTransition = useCallback(
    async (s3Key, agentId, documentId, fromMode, toMode) => {
      if (!networkId || !idToken) return;
      if (fromMode === toMode) return;

      setByKey((prev) => ({
        ...prev,
        [s3Key]: {
          ...(prev[s3Key] || defaultEntry()),
          syncing: true,
          pendingTarget: toMode,
          fromMode,
          awaitingTaskId: null,
          expectedStatus: null
        }
      }));

      const fail = (err) => {
        console.error('Memory transition failed:', err);
        setByKey((prev) => {
          const cur = prev[s3Key] || defaultEntry();
          return {
            ...prev,
            [s3Key]: {
              ...cur,
              syncing: false,
              pendingTarget: null,
              fromMode: null,
              awaitingTaskId: null,
              expectedStatus: null
            }
          };
        });
        clearFallback(s3Key);
      };

      const conn = connectionId || undefined;
      const coldCtx = parseMemoryColdContext(s3Key);
      const bodyBase = { s3Key, documentId, connectionId: conn };

      try {
        let awaitingTaskId = null;
        let expectedStatus = null;

        if (toMode === 'off' && fromMode === 'hot') {
          const r = await unsyncMemory(networkId, agentId, documentId, 'hot', idToken, conn);
          awaitingTaskId = r.taskId;
          expectedStatus = 'HOT_UNSYNC_COMPLETED';
        } else if (toMode === 'off' && fromMode === 'cold') {
          const r = await unsyncMemory(networkId, agentId, documentId, 'cold', idToken, conn, coldCtx);
          awaitingTaskId = r.taskId;
          expectedStatus = 'COLD_UNSYNC_COMPLETED';
        } else if (fromMode === 'off' && toMode === 'hot') {
          const r = await syncMemory(networkId, agentId, { ...bodyBase, layer: 'hot' }, idToken);
          awaitingTaskId = r.taskId;
          expectedStatus = 'HOT_SYNC_COMPLETED';
        } else if (fromMode === 'off' && toMode === 'cold') {
          const r = await syncMemory(
            networkId,
            agentId,
            { ...bodyBase, layer: 'cold', ...coldCtx },
            idToken
          );
          awaitingTaskId = r.taskId;
          expectedStatus = 'COLD_SYNC_COMPLETED';
        } else if (fromMode === 'hot' && toMode === 'cold') {
          await unsyncMemory(networkId, agentId, documentId, 'hot', idToken, conn);
          const r = await syncMemory(
            networkId,
            agentId,
            { ...bodyBase, layer: 'cold', ...coldCtx },
            idToken
          );
          awaitingTaskId = r.taskId;
          expectedStatus = 'COLD_SYNC_COMPLETED';
        } else if (fromMode === 'cold' && toMode === 'hot') {
          await unsyncMemory(networkId, agentId, documentId, 'cold', idToken, conn, coldCtx);
          const r = await syncMemory(networkId, agentId, { ...bodyBase, layer: 'hot' }, idToken);
          awaitingTaskId = r.taskId;
          expectedStatus = 'HOT_SYNC_COMPLETED';
        } else {
          fail(new Error(`Unsupported transition ${fromMode} -> ${toMode}`));
          return;
        }

        setByKey((prev) => ({
          ...prev,
          [s3Key]: {
            ...(prev[s3Key] || defaultEntry()),
            syncing: true,
            pendingTarget: toMode,
            fromMode,
            awaitingTaskId,
            expectedStatus
          }
        }));

        scheduleFallback(s3Key, expectedStatus, toMode);
      } catch (e) {
        fail(e);
      }
    },
    [networkId, idToken, connectionId, scheduleFallback, clearFallback]
  );

  const selectSegment = useCallback(
    (s3Key, agentId, documentId, currentSegment, nextSegment) => {
      if (!networkId || !idToken) return;
      const entry = byKeyRef.current[s3Key] || defaultEntry();
      if (entry.syncing) return;
      runTransition(s3Key, agentId, documentId, currentSegment, nextSegment);
    },
    [networkId, idToken, runTransition]
  );

  return { byKey, selectSegment };
}
